// src/pages/admin/FeatureCatalogAdmin.jsx
import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import LoadingSpinner from "../../components/LoadingSpinner";
import { toast } from "react-toastify";
import {
  getFeatureCatalog,
  upsertFeatureCategory,
  deleteFeatureCategory,
  deleteFeature,
  renameCategory,
  renameFeature,
} from "../../api/featureCatalog";
import { PlusIcon, TrashIcon, PencilIcon } from "@heroicons/react/24/outline";

export default function FeatureCatalogAdmin() {
  const qc = useQueryClient();

  // 1) fetch the catalog
  const { data: serverCats = [], isLoading } = useQuery({
    queryKey: ["featureCatalog"],
    queryFn: getFeatureCatalog,
  });

  // local editable copy, now with originals
  const [catalog, setCatalog] = useState([]);

  // sync into local state whenever server data arrives
  useEffect(() => {
    setCatalog(
      serverCats
        .slice()
        .reverse()
        .map((c) => ({
          category: c.category,
          originalCategory: c.category,
          features: c.features,
          originalFeatures: [...c.features],
        }))
    );
  }, [serverCats]);

  // handlers to edit local state
  const updateCategoryName = (idx, newName) =>
    setCatalog((cats) =>
      cats.map((c, i) => (i === idx ? { ...c, category: newName } : c))
    );
  const updateFeature = (cIdx, fIdx, newFeat) =>
    setCatalog((cats) =>
      cats.map((c, i) =>
        i === cIdx
          ? {
              ...c,
              features: c.features.map((f, j) => (j === fIdx ? newFeat : f)),
            }
          : c
      )
    );

  // bump originals after successful rename
  const updateOriginalCategory = (idx, newName) =>
    setCatalog((cats) =>
      cats.map((c, i) => (i === idx ? { ...c, originalCategory: newName } : c))
    );
  const updateOriginalFeature = (cIdx, fIdx, newFeat) =>
    setCatalog((cats) =>
      cats.map((c, i) =>
        i === cIdx
          ? {
              ...c,
              originalFeatures: c.originalFeatures.map((f, j) =>
                j === fIdx ? newFeat : f
              ),
            }
          : c
      )
    );

  // upsert category (for new only)
  const { mutate: saveCategory, isLoading: isSaving } = useMutation({
    mutationFn: upsertFeatureCategory,
    onSuccess: () => {
      qc.invalidateQueries(["featureCatalog"]);
      toast.success("Category saved");
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Save failed");
    },
  });

  // delete category
  const { mutate: deleteCategory, isLoading: isDeleting } = useMutation({
    mutationFn: deleteFeatureCategory,
    onSuccess: () => {
      qc.invalidateQueries(["featureCatalog"]);
      toast.success("Category deleted");
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Delete failed");
    },
  });

  // delete a single feature
  const { mutate: deleteFeatureMut, isLoading: isDeletingFeat } = useMutation({
    mutationFn: ({ category, feature }) => deleteFeature(category, feature),
    onSuccess: () => {
      qc.invalidateQueries(["featureCatalog"]);
      toast.success("Feature deleted globally");
    },
    onError: (err) =>
      toast.error(err.response?.data?.message || "Delete feature failed"),
  });

  // rename category globally
  const { mutate: renameCategoryMut, isLoading: renamingCat } = useMutation({
    mutationFn: ({ oldCategory, newCategory }) =>
      renameCategory(oldCategory, newCategory),
    onSuccess: (_data, { newCategory, idx }) => {
      qc.invalidateQueries(["featureCatalog"]);
      toast.success("Category renamed globally");
      updateOriginalCategory(idx, newCategory);
    },
    onError: (err) =>
      toast.error(err.response?.data?.message || "Rename category failed"),
  });

  // rename feature globally
  const { mutate: renameFeatureMut, isLoading: renamingFeat } = useMutation({
    mutationFn: ({ category, oldFeature, newFeature }) =>
      renameFeature(category, oldFeature, newFeature),
    onSuccess: (_data, { newFeature, cIdx, fIdx }) => {
      qc.invalidateQueries(["featureCatalog"]);
      toast.success("Feature renamed globally");
      updateOriginalFeature(cIdx, fIdx, newFeature);
    },
    onError: (err) =>
      toast.error(err.response?.data?.message || "Rename feature failed"),
  });

  // add / remove locally
  const addCategory = () =>
    setCatalog((cats) => [
      {
        category: "",
        originalCategory: "",
        features: [""],
        originalFeatures: [""],
      },
      ...cats,
    ]);
  const addFeature = (cIdx) =>
    setCatalog((cats) =>
      cats.map((c, i) =>
        i === cIdx ? { ...c, features: [...c.features, ""] } : c
      )
    );
  const removeFeature = (cIdx, fIdx) => {
    const { category, features } = catalog[cIdx];
    const featureToRemove = features[fIdx];
    // optimistically remove locally
    setCatalog((cats) =>
      cats.map((c, i) =>
        i === cIdx
          ? {
              ...c,
              features: c.features.filter((_, j) => j !== fIdx),
            }
          : c
      )
    );
    // then propagate globally
    deleteFeatureMut({ category, feature: featureToRemove });
  };

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="p-6">
      <div className="mb-4 flex justify-between mx-auto">
        <h2 className="text-2xl font-bold mb-5">Manage Feature Catalog</h2>
        <button
          onClick={addCategory}
          className="inline-flex items-center px-4 py-2 bg-surface rounded hover:bg-accent"
        >
          <PlusIcon className="h-5 w-5 mr-2" /> Add Category
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mx-auto">
        {catalog.map((cat, idx) => (
          <div
            key={idx}
            className="p-4 border rounded bg-surface dark:bg-darksec"
          >
            {/* Category row */}
            <div className="flex items-center mb-3 space-x-2">
              <input
                type="text"
                value={cat.category}
                onChange={(e) => updateCategoryName(idx, e.target.value)}
                placeholder="Category name"
                className="flex-1 border px-3 py-2 rounded text-primary"
              />

              {/* only show Save for brand-new categories */}
              {cat.originalCategory === "" && (
                <button
                  onClick={() =>
                    saveCategory({
                      category: cat.category,
                      features: cat.features,
                    })
                  }
                  disabled={!cat.category.trim() || isSaving}
                  className="flex items-center px-3 py-1 bg-accent text-white rounded hover:bg-primary/90 disabled:opacity-50"
                >
                  Save
                </button>
              )}

              {/* Rename category */}
              {cat.originalCategory !== "" && (
                <button
                  onClick={() =>
                    renameCategoryMut({
                      oldCategory: cat.originalCategory,
                      newCategory: cat.category,
                      idx,
                    })
                  }
                  disabled={
                    renamingCat ||
                    !cat.category.trim() ||
                    cat.category === cat.originalCategory
                  }
                  className="p-2 hover:bg-gray-200 rounded"
                  title="Rename this category globally"
                >
                  <PencilIcon className="h-5 w-5 text-blue-500" />
                </button>
              )}

              {/* Delete or Cancel */}
              {cat.originalCategory !== "" ? (
                <button
                  onClick={() => deleteCategory(cat.category)}
                  disabled={isDeleting}
                  className="p-2 text-red-500 hover:bg-red-100 rounded"
                  title="Delete this category globally"
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              ) : (
                <button
                  onClick={() =>
                    setCatalog((cats) => cats.filter((_, i) => i !== idx))
                  }
                  className="px-3 py-1 text-sm text-white hover:bg-red-300 rounded"
                >
                  Cancel
                </button>
              )}
            </div>

            {/* Features list */}
            <div className="pl-4">
              {cat.features.map((feat, fIdx) => (
                <div key={fIdx} className="flex items-center mb-2 space-x-2">
                  <input
                    type="text"
                    value={feat}
                    onChange={(e) => updateFeature(idx, fIdx, e.target.value)}
                    placeholder="Feature"
                    className="flex-1 border px-3 py-2 rounded text-primary"
                  />

                  {/* Rename feature */}
                  <button
                    onClick={() =>
                      renameFeatureMut({
                        category: cat.category,
                        oldFeature: cat.originalFeatures[fIdx],
                        newFeature: feat,
                        cIdx: idx,
                        fIdx,
                      })
                    }
                    disabled={
                      renamingFeat ||
                      !feat.trim() ||
                      feat === cat.originalFeatures[fIdx]
                    }
                    className="p-1 hover:bg-gray-200 rounded"
                    title="Rename this feature globally"
                  >
                    <PencilIcon className="h-5 w-5 text-blue-500" />
                  </button>

                  {/* Delete feature */}
                  <button
                    onClick={() => removeFeature(idx, fIdx)}
                    className="text-red-500 hover:bg-red-100 rounded p-1"
                    title="Delete this feature globally"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              ))}

              <button
                onClick={() => addFeature(idx)}
                className="mt-1 inline-flex items-center text-sm text-white hover:underline"
              >
                <PlusIcon className="h-4 w-4 mr-1" /> Add Feature
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
